// _sys/blocks/primitives/ComparisonChart.tsx
"use client";

import React from "react";
import styled, { useTheme } from "styled-components";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import type { ComparisonChartBlock } from "../../_sys/_types";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { resolveSemanticColor, colorFromIndex } from "../blocks/shared";

const Wrapper = styled.div`
  margin: ${({ theme }) => theme.spacing.large} 0;
`;

const BlockTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.title};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.medium} 0;
`;

const ChartCard = styled.div`
  background: ${({ theme }) => theme.colors.primaryBackground};
  border: ${({ theme }) => theme.borders.width.thin} solid
    ${({ theme }) => theme.colors.secondaryBackground};
  border-radius: ${({ theme }) => theme.borders.radius.medium};
  padding: ${({ theme }) => theme.spacing.medium};
`;

const Caption = styled.p`
  margin: ${({ theme }) => theme.spacing.small} 0 0 0;
  font-family: ${({ theme }) => theme.typography.fonts.regularItalic};
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const ComparisonChart: React.FC<{ block: ComparisonChartBlock }> = ({
  block,
}) => {
  const theme = useTheme();
  const containerRef = useScrollReveal({ selector: "self", y: 30 });

  const gridColor = theme.colors.secondaryBackground;
  const axisColor = theme.colors.textSecondary;
  const tooltipStyle = {
    background: theme.colors.static.black,
    border: "none",
    borderRadius: theme.borders.radius.small,
    color: theme.colors.static.white,
    fontFamily: theme.typography.fonts.regular,
    fontSize: theme.typography.sizes.hint,
  };
  const labelStyle = { color: theme.colors.static.white };

  const seriesColor = (
    idx: number,
    colorKey?: (typeof block.series)[number]["colorKey"],
  ) =>
    colorKey
      ? resolveSemanticColor(theme, colorKey)
      : colorFromIndex(theme, idx);

  return (
    <Wrapper>
      {block.title && <BlockTitle>{block.title}</BlockTitle>}
      <ChartCard ref={containerRef}>
        <ResponsiveContainer width="100%" height={320}>
          {block.chartType === "radar" ? (
            <RadarChart data={block.data} cx="50%" cy="50%" outerRadius="75%">
              <PolarGrid stroke={gridColor} />
              <PolarAngleAxis
                dataKey={block.categoryKey}
                tick={{
                  fill: axisColor,
                  fontSize: 12,
                  fontFamily: theme.typography.fonts.regular,
                }}
              />
              <PolarRadiusAxis tick={{ fill: axisColor, fontSize: 10 }} />
              {block.series.map((s, idx) => (
                <Radar
                  key={s.dataKey}
                  name={s.name}
                  dataKey={s.dataKey}
                  stroke={seriesColor(idx, s.colorKey)}
                  fill={seriesColor(idx, s.colorKey)}
                  fillOpacity={0.35}
                />
              ))}
              <Tooltip contentStyle={tooltipStyle} labelStyle={labelStyle} />
              <Legend
                wrapperStyle={{
                  fontFamily: theme.typography.fonts.medium,
                  fontSize: 12,
                  color: theme.colors.textPrimary,
                }}
              />
            </RadarChart>
          ) : block.chartType === "line" ? (
            <LineChart data={block.data}>
              <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
              <XAxis
                dataKey={block.categoryKey}
                tick={{
                  fill: axisColor,
                  fontSize: 12,
                  fontFamily: theme.typography.fonts.regular,
                }}
              />
              <YAxis
                tick={{
                  fill: axisColor,
                  fontSize: 12,
                  fontFamily: theme.typography.fonts.regular,
                }}
              />
              <Tooltip contentStyle={tooltipStyle} labelStyle={labelStyle} />
              <Legend
                wrapperStyle={{
                  fontFamily: theme.typography.fonts.medium,
                  fontSize: 12,
                  color: theme.colors.textPrimary,
                }}
              />
              {block.series.map((s, idx) => (
                <Line
                  key={s.dataKey}
                  type="monotone"
                  name={s.name}
                  dataKey={s.dataKey}
                  stroke={seriesColor(idx, s.colorKey)}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              ))}
            </LineChart>
          ) : (
            <BarChart data={block.data}>
              <CartesianGrid
                stroke={gridColor}
                strokeDasharray="3 3"
                vertical={false}
              />
              <XAxis
                dataKey={block.categoryKey}
                tick={{
                  fill: axisColor,
                  fontSize: 12,
                  fontFamily: theme.typography.fonts.regular,
                }}
              />
              <YAxis
                tick={{
                  fill: axisColor,
                  fontSize: 12,
                  fontFamily: theme.typography.fonts.regular,
                }}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelStyle={labelStyle}
                cursor={{ fill: theme.colors.semitransparent }}
              />
              <Legend
                wrapperStyle={{
                  fontFamily: theme.typography.fonts.medium,
                  fontSize: 12,
                  color: theme.colors.textPrimary,
                }}
              />
              {block.series.map((s, idx) => (
                <Bar
                  key={s.dataKey}
                  name={s.name}
                  dataKey={s.dataKey}
                  fill={seriesColor(idx, s.colorKey)}
                  radius={[6, 6, 0, 0]}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </ChartCard>
      {block.caption && <Caption>{block.caption}</Caption>}
    </Wrapper>
  );
};
